import { RefObject, useCallback, useEffect, useRef } from "react";

export type UseLoadMoreSentinelOptions = {
  /** Whether there's a next page to fetch — when `false`, the caller
   * shouldn't even render the `<InfiniteScrollSentinel>` this feeds, but
   * this hook doesn't gate on it itself (the sentinel unmounting when
   * `hasMore` flips false is what actually stops observation). */
  hasMore: boolean;
  /** True while a page fetch is already in flight — read by the caller to
   * decide whether to render `<SkeletonRow>`s, not used internally here
   * (the caller's real `fetchNextPage` already no-ops while pending). */
  isLoadingMore: boolean;
  /** Called (no args) when the sentinel scrolls into view — for a real,
   * BFF-backed table this is typically `fetchNextPage` from
   * `useInfiniteQuery`. */
  onLoadMore: () => void;
  /** The actual scrolling element (from `useScrollEdges`'s `ref`) — the
   * IntersectionObserver watches the sentinel against THIS as its root,
   * not the viewport, since these tables scroll within a contained div. */
  scrollRootRef: RefObject<HTMLElement | null>;
};

/**
 * Watches a sentinel element (rendered via `<InfiniteScrollSentinel>`) and
 * invokes `onLoadMore` — a real
 * `useInfiniteQuery().fetchNextPage` — whenever it scrolls into view of
 * `scrollRootRef`'s element. Use this for tables backed by a BFF endpoint
 * that supports cursor pagination.
 */
export function useLoadMoreSentinel({
  onLoadMore,
  scrollRootRef,
}: UseLoadMoreSentinelOptions) {
  const observerRef = useRef<IntersectionObserver | null>(null);
  // Ref-wrapped so the observer callback always calls the LATEST
  // `onLoadMore` (e.g. a fresh `fetchNextPage` closure from react-query)
  // without needing to recreate the IntersectionObserver every render.
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  // A callback ref, not a plain `useRef` + `useEffect`: the sentinel is
  // rendered conditionally, so it can mount/unmount after the first render.
  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      if (!node) return;

      const root = scrollRootRef.current;
      if (!root) return;

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) onLoadMoreRef.current();
        },
        { root, rootMargin: "300px" },
      );
      observer.observe(node);
      observerRef.current = observer;
    },
    [scrollRootRef],
  );

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return sentinelRef;
}
